import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Truck, TrendingUp, TrendingDown, AlertTriangle, Fuel, Users, Globe, DollarSign, FileText } from 'lucide-react';
import { useLanguage, LanguageSwitcher } from '@/components/LanguageContext';

const Article6 = () => {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-white">
      <div className="container mx-auto px-6 py-8">

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Badge className="bg-red-600 text-white mr-4">
              <Truck className="h-3 w-3 mr-1" />
              Road Freight
            </Badge>
            <span className="text-sm text-gray-600">Trans.info / IRU 2026 Road Freight Briefing</span>
          </div>
          <div className="flex items-center text-sm text-muted-foreground mb-6">
            <Calendar className="h-4 w-4 mr-1" />
            April 2026
            <span className="mx-2">•</span>
            <span>7 min read</span>
          </div>
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            {t('article.6.title')}
          </h1>

          <img
            src="https://cdn-trans.info/uploads/2025/07/bfd1ffbbb50e1b2f1351bda8bf6.webp"
            alt="EU road freight haulier truck"
            className="w-full h-64 object-cover rounded-lg mb-8"
          />

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 font-medium">
              {t('article.6.intro')}
            </p>

            {/* Key Stats */}
            <div className="bg-red-50 p-6 rounded-lg my-8 border border-red-200">
              <h3 className="font-bold text-red-900 mb-4">
                {language === 'ko' ? '핵심 수치 (IRU 2026 브리핑)' : 'Key Figures (IRU 2026 Briefing)'}
              </h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div className="text-center">
                  <h4 className="font-semibold mb-1 flex items-center justify-center text-red-800">
                    <Fuel className="mr-1 h-4 w-4" />
                    {language === 'ko' ? 'EU 경유 가격 급등' : 'EU Diesel Surge'}
                  </h4>
                  <p className="text-3xl font-bold text-red-600">+20%</p>
                  <p className="text-xs text-gray-500">{language === 'ko' ? '전쟁 발발 이후 (EU 전역)' : 'post-war outbreak (EU-wide)'}</p>
                </div>
                <div className="text-center">
                  <h4 className="font-semibold mb-1 flex items-center justify-center text-red-800">
                    <Users className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '운전기사 부족률' : 'Driver Shortage'}
                  </h4>
                  <p className="text-3xl font-bold text-orange-600">12.1%</p>
                  <p className="text-xs text-gray-500">{language === 'ko' ? '2025년 미충원 직위' : 'unfilled positions 2025'}</p>
                </div>
                <div className="text-center">
                  <h4 className="font-semibold mb-1 flex items-center justify-center text-red-800">
                    <TrendingUp className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '2024년 도로화물' : 'Road Freight 2024'}
                  </h4>
                  <p className="text-3xl font-bold text-blue-600">2.08T</p>
                  <p className="text-xs text-gray-500">{language === 'ko' ? '톤-킬로미터 (역대 3위)' : 'tonne-km (3rd highest ever)'}</p>
                </div>
              </div>
            </div>

            {/* Section 1 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.6.section1.title')}
            </h2>
            <p className="mb-6">{t('article.6.p1')}</p>
            <p className="mb-6">{t('article.6.p2')}</p>

            {/* Diesel Price Table */}
            <div className="bg-gray-50 p-6 rounded-lg my-8 border">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                <Fuel className="mr-2 h-5 w-5 text-red-600" />
                {language === 'ko' ? 'EU 주요국 경유 가격 현황 (2026년 3월 중순)' : 'EU Diesel Pump Prices — Mid-March 2026'}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-100">
                      <th className="text-left py-2 px-3">{language === 'ko' ? '국가' : 'Country'}</th>
                      <th className="text-left py-2 px-3">{language === 'ko' ? '가격 (리터당)' : 'Price (per litre)'}</th>
                      <th className="text-left py-2 px-3">{language === 'ko' ? '비고' : 'Note'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b bg-red-50">
                      <td className="py-2 px-3 font-semibold">🇮🇪 {language === 'ko' ? '아일랜드' : 'Ireland'}</td>
                      <td className="py-2 px-3 text-red-600 font-bold">€2.30/L</td>
                      <td className="py-2 px-3 text-xs text-red-600">{language === 'ko' ? 'EU 최고 가격' : 'EU highest pump price'}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-3 font-semibold">🇫🇮 {language === 'ko' ? '핀란드' : 'Finland'}</td>
                      <td className="py-2 px-3 text-orange-600 font-bold">&gt;€2.00/L</td>
                      <td className="py-2 px-3 text-xs">{language === 'ko' ? '€2 돌파' : 'Above €2 threshold'}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-3 font-semibold">🇫🇷 {language === 'ko' ? '프랑스' : 'France'}</td>
                      <td className="py-2 px-3 text-orange-600 font-bold">&gt;€2.00/L</td>
                      <td className="py-2 px-3 text-xs">{language === 'ko' ? '€2 돌파' : 'Above €2 threshold'}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-3 font-semibold">🇩🇪 {language === 'ko' ? '독일' : 'Germany'}</td>
                      <td className="py-2 px-3 text-orange-600 font-bold">&gt;€2.00/L</td>
                      <td className="py-2 px-3 text-xs">{language === 'ko' ? '€2 돌파' : 'Above €2 threshold'}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-3 font-semibold">🇮🇹 {language === 'ko' ? '이탈리아' : 'Italy'}</td>
                      <td className="py-2 px-3 text-orange-600 font-bold">&gt;€2.00/L</td>
                      <td className="py-2 px-3 text-xs">{language === 'ko' ? '€2 돌파' : 'Above €2 threshold'}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-3 font-semibold">🇳🇱 {language === 'ko' ? '네덜란드' : 'Netherlands'}</td>
                      <td className="py-2 px-3 text-orange-600 font-bold">&gt;€2.00/L</td>
                      <td className="py-2 px-3 text-xs">{language === 'ko' ? '€2 돌파' : 'Above €2 threshold'}</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-3 font-semibold">🇪🇸 {language === 'ko' ? '스페인' : 'Spain'}</td>
                      <td className="py-2 px-3 text-red-600 font-bold">↑ {language === 'ko' ? '최대 상승폭' : 'Largest rise'}</td>
                      <td className="py-2 px-3 text-xs text-red-600">{language === 'ko' ? 'EU 내 최대 가격 상승폭 기록' : 'Largest price increase recorded in EU'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {language === 'ko' ? '출처: IRU 모니터링 데이터 (2026년 3월 중순 기준). EU 전역 평균 +20% 상승.' : 'Source: IRU monitoring data (mid-March 2026). EU-wide average +20% rise.'}
              </p>
            </div>

            {/* Section 2 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.6.section2.title')}
            </h2>
            <p className="mb-6">{t('article.6.p3')}</p>
            <p className="mb-6">{t('article.6.p4')}</p>

            {/* Regulatory Cost Roadmap */}
            <div className="bg-orange-50 p-6 rounded-lg my-8 border border-orange-200">
              <h3 className="font-bold text-orange-900 mb-4 flex items-center">
                <FileText className="mr-2 h-5 w-5" />
                {language === 'ko' ? 'EU 규제 비용 로드맵' : 'EU Regulatory Cost Roadmap'}
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start bg-white rounded p-3">
                  <span className="font-bold text-orange-600 mr-3 min-w-[60px]">2026</span>
                  <span>{language === 'ko' ? '네덜란드·루마니아: 유로비네트 관련 CO2 기반 통행료 변경 시행' : 'Netherlands & Romania: Eurovignette CO2-based tolling changes roll out'}</span>
                </div>
                <div className="flex items-start bg-white rounded p-3">
                  <span className="font-bold text-orange-600 mr-3 min-w-[60px]">2028</span>
                  <span>{language === 'ko' ? 'ETS2 시행 예정: 도로 운송 화석연료에 탄소 비용 적용 (CO2 톤당 €50~100 → 경유 펌프 가격 +약 10%)' : 'ETS2 scheduled: Carbon cost embedded in fossil fuels for road transport (€50–100/t CO2 → pump price +~10%)'}</span>
                </div>
                <div className="flex items-start bg-white rounded p-3">
                  <span className="font-bold text-orange-600 mr-3 min-w-[60px]">2030s</span>
                  <span>{language === 'ko' ? '일부 전망: ETS2 탄소 가격 더욱 상승 가능 / 대형 차량 CO2 기준 강화 지속' : 'Some projections: ETS2 carbon prices potentially much higher / HDV CO2 standards tighten further'}</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                {language === 'ko' ? '※ IRU는 이 규제 변화들이 경유 차량 TCO(총소유비용)를 높이고 저배출 대안의 경제적 타당성을 강화할 것이라고 밝힙니다.' : '※ IRU notes these will raise total cost of ownership for diesel fleets and strengthen the economic case for lower-emission alternatives.'}
              </p>
            </div>

            {/* Section 3 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.6.section3.title')}
            </h2>
            <p className="mb-6">{t('article.6.p5')}</p>
            <p className="mb-6">{t('article.6.p6')}</p>

            {/* Capacity Metrics */}
            <div className="bg-blue-50 p-6 rounded-lg my-8 border border-blue-200">
              <h3 className="font-bold text-blue-900 mb-4 flex items-center">
                <TrendingDown className="mr-2 h-5 w-5" />
                {language === 'ko' ? '시장 용량 지표' : 'Market Capacity Indicators'}
              </h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm text-center">
                <div className="bg-white rounded p-3">
                  <p className="text-xs text-gray-500 mb-1">{language === 'ko' ? '신규 트럭 등록 (2025)' : 'New Truck Registrations (2025)'}</p>
                  <p className="text-2xl font-bold text-red-600">-6%</p>
                  <p className="text-xs">{language === 'ko' ? '전년 대비' : 'vs 2024'}</p>
                </div>
                <div className="bg-white rounded p-3">
                  <p className="text-xs text-gray-500 mb-1">{language === 'ko' ? '운전기사 미충원율 (2025)' : 'Driver Vacancies (2025)'}</p>
                  <p className="text-2xl font-bold text-orange-600">12.1%</p>
                  <p className="text-xs">{language === 'ko' ? '동유럽·독일·스페인 심각' : 'Worst in E.Europe, DE, ES'}</p>
                </div>
                <div className="bg-white rounded p-3">
                  <p className="text-xs text-gray-500 mb-1">{language === 'ko' ? '공차 운행률 (EU)' : 'Empty Running (EU)'}</p>
                  <p className="text-2xl font-bold text-green-600">~20%</p>
                  <p className="text-xs">{language === 'ko' ? '효율 개선 추세' : 'Improving efficiency trend'}</p>
                </div>
              </div>
              <div className="mt-4 bg-white rounded p-3 text-sm text-center">
                <p className="text-xs text-gray-500 mb-1">{language === 'ko' ? '평균 적재량 (탑재 운행당)' : 'Avg Payload (per loaded trip)'}</p>
                <p className="text-xl font-bold text-blue-600">14.3 {language === 'ko' ? '톤' : 'tonnes'}</p>
                <p className="text-xs text-gray-500">{language === 'ko' ? '지난 10년간 개선 추세' : 'Improved over the past decade'}</p>
              </div>
            </div>

            {/* Section 4 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.6.section4.title')}
            </h2>
            <p className="mb-6">{t('article.6.p7')}</p>
            <p className="mb-6">{t('article.6.p8')}</p>

            {/* Trade Exposure Table */}
            <div className="bg-gray-50 p-6 rounded-lg my-8 border">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                <Globe className="mr-2 h-5 w-5" />
                {language === 'ko' ? 'EU 대미(對美) 수출 노출도 — 관세 위험 부문' : 'EU Export Exposure to US — Tariff-Risk Sectors'}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-100">
                      <th className="text-left py-2 px-3">{language === 'ko' ? '부문' : 'Sector'}</th>
                      <th className="text-left py-2 px-3">{language === 'ko' ? '미국 수출 비중' : 'US Export Share'}</th>
                      <th className="text-left py-2 px-3">{language === 'ko' ? '노선 영향' : 'Corridor Impact'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 px-3 font-semibold">{language === 'ko' ? '기계·운송 장비' : 'Machinery & Transport Equipment'}</td>
                      <td className="py-2 px-3 text-red-600 font-bold">21%</td>
                      <td className="py-2 px-3 text-xs">{language === 'ko' ? '독일·이탈리아·중동부 유럽 산업 노선 취약' : 'DE/IT/CEE industrial corridors vulnerable'}</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-3 font-semibold">{language === 'ko' ? '화학' : 'Chemicals'}</td>
                      <td className="py-2 px-3 text-red-600 font-bold">31%</td>
                      <td className="py-2 px-3 text-xs">{language === 'ko' ? '화학 클러스터 연계 화물 노선 압박' : 'Pressure on corridors serving chemical clusters'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {language === 'ko' ? '출처: IRU 2026 도로화물 브리핑 — 기계·화학은 EU 2024년 무역흑자의 두 핵심 동력' : 'Source: IRU 2026 Road Freight Briefing — Machinery & chemicals were the two main drivers of EU 2024 trade surplus'}
              </p>
            </div>

            {/* Warning Box */}
            <div className="bg-red-50 p-6 rounded-lg my-8 border border-red-200">
              <h3 className="font-bold text-red-900 mb-3 flex items-center">
                <AlertTriangle className="mr-2 h-5 w-5" />
                {language === 'ko' ? 'IRU 경고: 노선별 격차 확대' : 'IRU Warning: Corridor Divergence Ahead'}
              </h3>
              <p className="text-sm text-gray-700">
                {language === 'ko'
                  ? '중국산 제품의 유럽 우회 유입 가능성과 미국 관세의 부문별 영향이 복합적으로 작용하면서, IRU는 유럽 도로화물 시장이 하나의 균일한 추세가 아닌 노선별로 더욱 엇갈린 흐름을 보일 것으로 전망합니다. 일부 노선은 무역 전환으로 수혜를 입는 반면, 다른 노선은 수요 압박에 직면할 수 있습니다.'
                  : 'With Chinese goods potentially diverted into Europe and US tariffs hitting specific industrial sectors, IRU forecasts that European road freight will see greater corridor divergence rather than a uniform market trend. Some corridors may benefit from trade diversion while others face demand pressure.'}
              </p>
            </div>

            {/* Key Takeaways */}
            <div className="bg-gray-50 p-6 rounded-lg mt-8">
              <h3 className="font-bold text-gray-900 mb-4">
                {language === 'ko' ? '주요 시사점' : 'Key Takeaways'}
              </h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start"><span className="mr-2 text-red-500">▲</span><span>{language === 'ko' ? '이란 전쟁 발발 이후 EU 경유 가격 +20% 급등 — 아일랜드 €2.3/L로 EU 최고 기록' : 'EU diesel prices +20% after Iran war outbreak — Ireland hits EU record at €2.3/litre'}</span></li>
                <li className="flex items-start"><span className="mr-2 text-red-500">▲</span><span>{language === 'ko' ? '핀란드·프랑스·독일·이탈리아·네덜란드 모두 리터당 €2 돌파' : 'Finland, France, Germany, Italy and Netherlands all above €2/litre'}</span></li>
                <li className="flex items-start"><span className="mr-2 text-orange-500">!</span><span>{language === 'ko' ? 'ETS2 (2028년 예정): 탄소 비용 내재화로 경유 가격 +10% 추가 압박 전망' : 'ETS2 (2028): Carbon costs to push diesel pump prices up ~10% further'}</span></li>
                <li className="flex items-start"><span className="mr-2 text-orange-500">!</span><span>{language === 'ko' ? '네덜란드·루마니아 2026년 유로비네트 CO2 기반 통행료 시행 예정' : 'Netherlands & Romania rolling out Eurovignette CO2-based tolling in 2026'}</span></li>
                <li className="flex items-start"><span className="mr-2 text-red-500">▼</span><span>{language === 'ko' ? '2025년 신규 트럭 등록 -6%, 운전기사 미충원 12.1% — 용량 이중 압박' : '2025 new truck registrations -6%, driver vacancies 12.1% — double capacity squeeze'}</span></li>
                <li className="flex items-start"><span className="mr-2 text-blue-500">→</span><span>{language === 'ko' ? '2025년 말 계약 운임이 현물 운임 추월 — 미래 용량 확보 경쟁 심화 신호' : 'Contract rates overtook spot in late 2025 — signal of growing capacity securing behaviour'}</span></li>
                <li className="flex items-start"><span className="mr-2 text-green-500">✓</span><span>{language === 'ko' ? '2024년 EU 도로화물 +0.5% 증가, 2.08조 tkm — 역대 3위 기록 달성' : '2024 EU road freight +0.5%, 2.08 trillion tonne-km — third-strongest year on record'}</span></li>
                <li className="flex items-start"><span className="mr-2 text-green-500">✓</span><span>{language === 'ko' ? '공차 운행률 ~20% 하락, 평균 적재량 14.3t — 운영 효율 개선 지속' : 'Empty running ~20%, avg payload 14.3 tonnes — operational efficiency continues to improve'}</span></li>
              </ul>
            </div>
          </div>

          {/* Source Attribution */}
          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              {t('common.sourceAttribution')} Trans.info / IRU — 2026 Road Freight Briefing: Costs, Capacity and Corridor Shifts.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t('common.sourceDetails')}
            </p>
            <a
              href="https://trans.info/en/iru-2026-costs-diesel-468759"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button className="bg-red-600 hover:bg-red-700 text-white">
                {t('common.readOriginalArticle')}
              </Button>
            </a>
          </div>
        </article>
      </div>
    </div>
  );
};

export default Article6;
